import { axiosRubyClient as api } from '@/util/axios/axiosClient';

export interface TierBoundary {
  tier: string;
  label: string;
  min: number;
  max: number;
}
export interface PreferredPool {
  customer_id: number;
  customer_name: string | null;
  kind: 'favorited' | 'lta';
  standing: string | null;
}
export interface TrendStat {
  current: number | null;
  previous: number | null;
  trend: 'up' | 'down' | 'flat' | null;
}

// GRAVY-10422/10420: an optional badge beside a signal's label — the late-drop
// magnitude (Low/Moderate/High) or the accept-rate "Below N% target" flag.
export interface DependabilitySignalPill {
  label: string;
  variant: 'healthy' | 'below_target';
}

export interface DependabilitySignal {
  key: string;
  label: string;
  percent: number | null;
  display_value: string;
  status: 'ok' | 'warn';
  // GRAVY-10422: the count line rendered under the bar ("44 of 47 shifts
  // arrived on time"). Optional + nullable so a backend that predates the
  // breakdown redesign (rolling deploy) still renders the row without a caption.
  caption?: string | null;
  // GRAVY-10422/10420: optional label pill. Absent/null on rows that carry none.
  pill?: DependabilitySignalPill | null;
  // GRAVY-10429: recency-weighting metadata for the breakdown modal. `weighted`
  // is true on the three recency-weighted rows (on_time, completion,
  // client_rating) — those rows show a "Weighted" pill and a purple caption
  // note. `raw_percent` is the simple (unweighted) percent for on_time &
  // completion and null for client_rating; it is null/absent on the plain rows.
  // Both optional so a backend that predates the fields (rolling deploy) still
  // renders the row with no pill and no note.
  weighted?: boolean;
  raw_percent?: number | null;
}

// GRAVY-10429: the activation-mode breakdown block. Emitted by the backend ONLY
// when scoring_mode === 'activation' (a worker with fewer than 5 approved
// shifts), so it is optional on the payload and absent for reliability workers.
export interface ActivationBreakdown {
  onboarding: {
    percent: number; // 0-100 integer (simple step count)
    steps_completed: number;
    steps_total: number;
    complete: boolean;
    // title of the next incomplete step; null once onboarding is complete
    pending_step: string | null;
    // GRAVY-10429: true when the pending step was REJECTED/FAILED (needs a redo)
    // rather than merely not-started — the caption then reads "… rejected"
    // instead of "… pending". Optional for rolling-deploy back-compat with a
    // backend that predates the field.
    pending_step_rejected?: boolean;
  };
  shifts: {
    completed: number;
    required: number; // graduation threshold (5)
    remaining: number;
    percent: number; // min(100, completed/required*100), integer
    // true while onboarding is incomplete — the worker cannot claim shifts yet
    locked: boolean;
  };
  // Figma 40013-12759: the activation overview-card chips — one onboarding
  // milestone per entry, painted green (complete) or grey (pending). Optional
  // for rolling-deploy back-compat with a backend that predates the field.
  milestones?: {
    key: string;
    label: string;
    complete: boolean;
  }[];
  account_created_at: string; // ISO8601
}

export interface DependabilityV2 {
  score: number;
  tier: string;
  tier_label: string;
  scoring_mode: 'activation' | 'reliability';
  computed_at: string;
  completed_shifts_count: number;
  // GRAVY-10404: DISPLAY-ONLY suspension signal. When suspended/permanently
  // removed, the tier badge shows "Suspended" instead of the tier. Optional so a
  // backend that predates the field still renders the tier normally.
  accountability_status?: 'active' | 'suspended' | 'permanently_removed';
  // GRAVY-10404: authoritative active-suspension flag (reflects an active
  // suspension record — temporary OR permanent — the same signal the suspension
  // banner uses). Drives the "Suspended" badge even when accountability_status
  // lags the suspension state. Optional for a backend that predates the field.
  is_suspended?: boolean;
  tier_boundaries: TierBoundary[];
  badges: {
    window_days: number;
    no_shows_count: number;
    // late ARRIVALS (check-ins past the grace window)
    late_count: number;
    // GRAVY-10402: all-time active late-drop (late cancellation) violations.
    // Optional so a backend that predates the field (rolling deploy) still
    // passes the isDependabilityV2 shape check; the UI defaults it to 0.
    late_cancels_count?: number;
    preferred_pools: PreferredPool[];
  };
  stats: {
    shifts_completed: number;
    on_time_rate: TrendStat;
    client_rating: TrendStat;
  };
  legacy: { tier: string; rating: number; label: string } | null;
  // optional so the UI tolerates a backend that predates the signals field
  signals?: DependabilitySignal[];
  // GRAVY-10429: present ONLY for activation workers (scoring_mode ===
  // 'activation'). Reliability payloads omit it, so it stays optional and the
  // runtime guard (isDependabilityV2) does not require it.
  activation_breakdown?: ActivationBreakdown;
}

export interface DependabilityEvent {
  id: number;
  event_type: string;
  category: 'positive' | 'negative' | 'neutral';
  description: string;
  delta: number | null;
  score_after: number;
  occurred_at: string;
}

export interface DependabilityEventsResponse {
  success: boolean;
  data: DependabilityEvent[];
  pagination: {
    total: number;
    page: number;
    per_page: number;
    total_pages: number;
  };
}

// Runtime shape check for the dependability payload (fix round 3): during a
// rolling deploy (or any divergent backend revision) a success:true payload
// can arrive missing tier_boundaries / badges.preferred_pools /
// stats.on_time_rate — shapes that throw during render (TierPopover is
// mounted even while closed) and, with no error boundary in the app, blank
// the entire WorkerV2 route. Callers must fall back to the legacy card when
// this returns false. Only fields the components dereference unconditionally
// are checked; `signals` stays optional by design.
export const isDependabilityV2 = (data: unknown): data is DependabilityV2 => {
  if (!data || typeof data !== 'object') return false;
  const d = data as Record<string, any>;
  return (
    typeof d.score === 'number' &&
    typeof d.tier === 'string' &&
    Array.isArray(d.tier_boundaries) &&
    !!d.badges &&
    typeof d.badges === 'object' &&
    Array.isArray(d.badges.preferred_pools) &&
    typeof d.badges.window_days === 'number' &&
    typeof d.badges.no_shows_count === 'number' &&
    typeof d.badges.late_count === 'number' &&
    !!d.stats &&
    typeof d.stats === 'object' &&
    !!d.stats.on_time_rate &&
    !!d.stats.client_rating
  );
};

export const dependabilityV2API = {
  getDependability: async (
    workerId: number,
  ): Promise<{ success: boolean; data: DependabilityV2 }> =>
    (await api.get(`/api/admin/workers/${workerId}/dependability`)).data,

  getEvents: async (
    workerId: number,
    page = 1,
    perPage = 5,
  ): Promise<DependabilityEventsResponse> =>
    (
      await api.get(`/api/admin/workers/${workerId}/dependability/events`, {
        params: { page, per_page: perPage },
      })
    ).data,
};
